import React, { useEffect, useRef, useState } from "react";
import { X, Plus, Loader2, Wrench, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const LINES = ["Tramogge", "Gramole", "Cernitore", "Nastri", "Lavatrici", "Altro"];
export const STOP_CAUSES = ["Guasto", "Manutenzione", "Cambio formato", "Mancanza materiale", "Problema qualità", "Altro"];
export const today = () => new Date().toISOString().slice(0, 10);

export function Modal({ title, onClose, children, testid, wide }) {
  const down = useRef(false);
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onMouseDown={(e) => (down.current = e.target === e.currentTarget)}
      onClick={() => {}}>
      <div data-testid={testid} className={`bg-white rounded-[6px] w-full my-0 sm:my-auto ${wide ? "max-w-[620px]" : "max-w-[460px]"} max-h-[95vh] sm:max-h-[92vh] overflow-y-auto shadow-2xl`}>
        <div className="flex items-center justify-between px-4 py-3 border-b hairline sticky top-0 bg-white z-10">
          <span className="font-display font-700 t-head">{title}</span>
          <button data-testid="modal-close" onClick={onClose} className="p-1 t-muted hover:t-head"><X size={18} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export function StopModal({ causes = STOP_CAUSES, onClose, onSaved }) {
  const [form, setForm] = useState({ machine: "", cause: causes[0], date: today(), duration_min: "", cost: "", note: "" });
  const [machines, setMachines] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/production/machines").then(({ data }) => setMachines(data.machines || [])).catch(() => {}); }, []);
  const submit = async () => {
    if (!form.machine.trim() || !form.duration_min) { toast.error("Macchina e durata obbligatorie"); return; }
    setSaving(true);
    try {
      await api.post("/production/stops", { ...form, duration_min: parseFloat(form.duration_min), cost: form.cost ? parseFloat(form.cost) : undefined });
      toast.success("Fermo registrato"); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };
  return (
    <Modal title="Registra fermo macchina" testid="stop-modal" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="lbl">Macchina *</label>
          <input data-testid="stop-machine" list="stop-machine-list" value={form.machine} onChange={(e) => setForm({ ...form, machine: e.target.value })} className="field w-full" placeholder="Es. Gramola 800" />
          <datalist id="stop-machine-list">{machines.map((m) => <option key={m} value={m} />)}</datalist>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="lbl">Causale *</label><select data-testid="stop-cause" value={form.cause} onChange={(e) => setForm({ ...form, cause: e.target.value })} className="field w-full">{causes.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className="lbl">Data</label><input data-testid="stop-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="field w-full" /></div>
          <div><label className="lbl">Durata (min) *</label><input data-testid="stop-duration" type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="field w-full" /></div>
        </div>
        <div><label className="lbl">Costo riparazione € (opzionale)</label><input data-testid="stop-cost" type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="field w-full" /></div>
        <div><label className="lbl">Note</label><textarea data-testid="stop-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="field w-full" /></div>
        <button data-testid="stop-submit" onClick={submit} disabled={saving} className="btn-industrial w-full py-2.5 rounded-[3px] bg-[#9c1e2b] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Registra fermo</button>
      </div>
    </Modal>
  );
}

export function ScrapModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ product: "", qty: "", cause: "", date: today(), cost: "", note: "" });
  const [machines, setMachines] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/production/machines").then(({ data }) => setMachines(data.machines || [])).catch(() => {}); }, []);
  const submit = async () => {
    if (!form.qty || parseInt(form.qty, 10) <= 0) { toast.error("Quantità scarti obbligatoria"); return; }
    setSaving(true);
    try {
      await api.post("/production/scrap", { ...form, qty: parseInt(form.qty, 10), cost: form.cost ? parseFloat(form.cost) : undefined });
      toast.success("Scarto registrato"); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };
  return (
    <Modal title="Registra scarto / problema" testid="scrap-modal" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="lbl">Prodotto / macchina</label>
          <input data-testid="scrap-product" list="scrap-machine-list" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="field w-full" placeholder="Es. Gramola 800" />
          <datalist id="scrap-machine-list">{machines.map((m) => <option key={m} value={m} />)}</datalist>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="lbl">Quantità *</label><input data-testid="scrap-qty" type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="field w-full" /></div>
          <div><label className="lbl">Costo € (opz.)</label><input data-testid="scrap-cost" type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="field w-full" /></div>
          <div><label className="lbl">Data</label><input data-testid="scrap-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="field w-full" /></div>
        </div>
        <div><label className="lbl">Causale</label><input data-testid="scrap-cause" value={form.cause} onChange={(e) => setForm({ ...form, cause: e.target.value })} className="field w-full" placeholder="Es. saldatura difettosa" /></div>
        <div><label className="lbl">Note</label><textarea data-testid="scrap-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="field w-full" /></div>
        <button data-testid="scrap-submit" onClick={submit} disabled={saving} className="btn-industrial w-full py-2.5 rounded-[3px] bg-[#b45309] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Registra scarto</button>
      </div>
    </Modal>
  );
}

export function EntryButtons({ onSaved, big }) {
  const [modal, setModal] = useState(null);
  const sz = big ? "px-4 py-3 min-h-[52px] text-base" : "px-3 py-1.5 text-sm";
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <button data-testid="btn-add-stop" onClick={() => setModal("stop")} className={`btn-industrial inline-flex items-center gap-1.5 rounded-[3px] bg-[#9c1e2b] text-white ${sz}`}><Wrench size={16} /> Fermo macchina</button>
        <button data-testid="btn-add-scrap" onClick={() => setModal("scrap")} className={`btn-industrial inline-flex items-center gap-1.5 rounded-[3px] bg-[#b45309] text-white ${sz}`}><AlertTriangle size={16} /> Scarto</button>
      </div>
      {modal === "stop" && <StopModal onClose={() => setModal(null)} onSaved={() => { setModal(null); onSaved && onSaved(); }} />}
      {modal === "scrap" && <ScrapModal onClose={() => setModal(null)} onSaved={() => { setModal(null); onSaved && onSaved(); }} />}
    </>
  );
}
